import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import { ReactComponent as ChevronSolidIcon } from "assets/images/icons/icn_chevron_solid.svg";
import { ReactComponent as DeleteIcon } from "assets/images/icons/icn_table_cell_Unverified.svg";
import btn_add from "assets/images/icons/btn_add.svg";

import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Collapse,
  Paper,
  TableContainer,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { cloneDeep } from "lodash";

import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import MacAddressInputField from "components/InputField/MacAddressInputField";

const macAddressItem = {
  open: true,
  macAddress: "",
  count: null,
  description: "",
}

const AddView = ({
  viewStateEnum,
  getViewTitle,
  form,
  handleAddView,
}) => {
  const theme = useTheme();

  // Status
  const [macAddressItemList, setMacAddressItemList] = useState([
    ...Array.from({ length: 1 }, () => cloneDeep(macAddressItem))
  ]);

  // Method
  const toggleOpenList = (index) => {
    const newList = cloneDeep(macAddressItemList);
    newList[index].open = !newList[index].open;
    setMacAddressItemList(newList);
  }

  const addNextItem = () => {
    const newList = cloneDeep(macAddressItemList);
    newList.push(cloneDeep(macAddressItem));
    setMacAddressItemList(newList);
  }

  const changeItemValue = (index, field, value) => {
    const newList = cloneDeep(macAddressItemList);
    newList[index][field] = value;
    setMacAddressItemList(newList);
  }

  const deleteItem = (index) => {
    const newList = cloneDeep(macAddressItemList);
    newList.splice(index, 1);
    setMacAddressItemList(newList);
  }

  const checkFormValid = () => {
    for (let item of macAddressItemList) {
      if (form.validateMacAddress(item.macAddress) === false || item.macAddress === "") {
        return false;
      }

      if (!item.count || form.validateCount(+item.count) === false || parseInt(item.count) < 1) {
        return false;
      }
    }

    return true;
  }

  const closeView = () => {
    setMacAddressItemList([
      ...Array.from({ length: 1 }, () => cloneDeep(macAddressItem))
    ]);
    handleAddView.closeView();
  }

  const handleSave = () => {
    const newMacAddresses = [];

    macAddressItemList.forEach((item) => {
      const count = parseInt(item.count) || 1;
      // AA:BB:CC:DD:EE:01 with count 3 will add
      // AA:BB:CC:DD:EE:01
      // AA:BB:CC:DD:EE:02
      // AA:BB:CC:DD:EE:03
      const generatedMacs = form.makeMacAddressItems(item.macAddress, count);
      generatedMacs.forEach((mac) => {
        newMacAddresses.push({
          macAddress: mac,
          description: item.description,
        });
      });
    });

    handleAddView.addMacAddresses(newMacAddresses);
    closeView();
  }

  // Side Effects
  useEffect(() => {
  }, []);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.customFontSize[5],
            fontWeight: theme.typography.customFontWeight[3],
            paddingLeft: "20px",
          }}
        >
          {getViewTitle(viewStateEnum.ADD)}
        </Typography>
        <Button
          onClick={closeView}
          sx={{
            backgroundColor: theme.palette.primary.transparent,
            boxShadow: "none",
            "&:hover": {
              backgroundColor: theme.palette.primary.transparent,
            },
          }}
        >
          <CloseIcon />
        </Button>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          "&.MuiPaper-root": {
            boxShadow: "none",
            border: 'none',
          },
        }}
        style={{
          maxHeight: '320px',
          marginLeft: "20px",
          width: "calc(100% - 40px)",
          overflowY: macAddressItemList.length > 2 ? 'auto' : 'hidden',
        }}
      >
        {
          macAddressItemList.map((item, index) => {
            return (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingTop: "20px",
                    cursor: "pointer",
                    height: '40px',
                    gap: "8px",
                  }}
                  onClick={() => toggleOpenList(index)}
                >
                  <Box sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    gap: "8px"
                  }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        transform: item.open ? "rotate(90deg)" : "rotate(0deg)",
                      }}
                    >
                      <ChevronSolidIcon />
                    </Box>
                    <Typography
                      sx={{
                        color: theme.palette.primary.greyPrimary,
                        fontSize: theme.typography.customFontSize[2],
                        fontWeight: theme.typography.customFontWeight[1],
                      }}
                    >
                      MAC Address({index + 1})
                    </Typography>
                  </Box>
                  {
                    index === 0 ? null : (
                      <>
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteItem(index);
                          }}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                            padding: "8px",
                          }}
                        >
                          <DeleteIcon />
                        </Box>
                      </>
                    )
                  }
                </Box>
                <Collapse
                  in={item.open}
                  timeout="auto"
                  unmountOnExit
                >
                  <Box sx={{ ...form.getRowStyle() }}>
                    <Box sx={form.getAddItemTopContainerStyle()}>
                      <Box
                        sx={{
                          ...form.getLabelStyle(),
                          marginLeft: '12px',
                        }}
                      >
                        MAC Address
                      </Box>
                      <MacAddressInputField
                        value={item.macAddress}
                        onChange={(newVal) => {
                          changeItemValue(index, 'macAddress', newVal);
                        }}
                        disabled={false}
                        showError={form.validateMacAddress(item.macAddress) === false && item.macAddress !== ""}
                      />
                      <Box style={{ textAlign: 'center' }}>
                        <Typography
                          sx={{
                            color: theme.palette.primary.greyPrimary,
                            fontSize: theme.typography.customFontSize[2],
                          }}
                        >
                          Count
                        </Typography>
                      </Box>
                      <TextField
                        placeholder=""
                        sx={form.getTextfieldStyle(true)}
                        value={item.count}
                        onChange={(e) => {
                          changeItemValue(index, 'count', e.target.value);
                        }}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ ...form.getRowStyle(), marginBottom: '10px' }}>
                    <Box sx={form.getAddItemTopContainerStyle()}>
                      <Box
                        sx={{
                          ...form.getLabelStyle(),
                          marginLeft: '12px',
                        }}
                      >
                        Description
                      </Box>
                      <TextField
                        placeholder=""
                        sx={form.getTextfieldStyle(true)}
                        value={item.description}
                        onChange={(e) => {
                          changeItemValue(index, 'description', e.target.value);
                        }}
                      />
                      <Box style={{ textAlign: 'center' }}>
                        <Typography
                          sx={{
                            color: theme.palette.primary.greyPrimary,
                            fontSize: theme.typography.customFontSize[2],
                          }}
                        >
                          (Optional)
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Collapse>
              </>
            );
          })
        }
      </TableContainer>
      <Box display={"flex"} justifyContent="flex-end" marginTop="20px">
        <Box
          style={form.getButtonContainerStyle()}
          onClick={addNextItem}
        >
          <Box style={form.getAddButtonStyle()}>
            <img
              src={btn_add}
              alt="Add"
            />
            <span>Add New MAC Address</span>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          margin: "24px 0 16px auto",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <OutlineButton
          onClick={closeView}
          sx={{ marginRight: "20px" }}
          width="100px"
          height="32px"
        >
          Cancel
        </OutlineButton>
        <SolidButton
          onClick={handleSave}
          width="100px"
          height="32px"
          disabled={!checkFormValid()}
        >
          Apply
        </SolidButton>
      </Box>
    </>
  )
}

export default React.memo(AddView);